import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Siren, Pencil, FileText, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PetAvatar } from "@/components/PetAvatar";
import { ScheduleDialog } from "@/components/ScheduleDialog";
import { VetRecords } from "@/components/VetRecords";
import { PetItems } from "@/components/PetItems";
import { ReminderDialog, ReminderRow, toggleReminder } from "@/components/Reminder";
import { GuidePet } from "@/components/GuideSheet";
import { api, errMsg, fmtTime } from "@/lib/api";
import { CARE_TYPES, DAYS, VET_LINE } from "@/lib/constants";

function ScheduleList({ petId, schedules, reload }) {
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const del = async (s) => { await api.delete(`/schedules/${s.id}`); toast("Removed"); reload(); };
  return (
    <div className="space-y-3">
      {schedules.map((s) => {
        const t = CARE_TYPES[s.type] || CARE_TYPES.other;
        const when = s.frequency === "weekly" ? s.days_of_week.map((d) => DAYS[d]).join(", ") : s.frequency === "monthly" ? `Monthly, day ${s.day_of_month}` : s.frequency === "interval" ? `Every ${s.interval_days} days` : "Daily";
        return (
          <div key={s.id} className="pl-card p-4 flex items-center gap-3" data-testid={`schedule-${s.id}`}>
            <span className="h-11 w-11 rounded-full grid place-items-center" style={{ background: t.bg, color: t.color }}><t.icon className="h-5 w-5" /></span>
            <div className="flex-1 min-w-0"><p className="font-semibold">{s.title}{s.amount ? ` · ${s.amount}` : ""}</p>
              <p className="text-xs text-slate-500">{when} · {(s.times.length ? s.times : ["anytime"]).map(fmtTime).join(", ")}</p></div>
            <button data-testid={`schedule-edit-${s.id}`} onClick={() => { setEdit(s); setOpen(true); }} className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`schedule-delete-${s.id}`} onClick={() => del(s)} className="h-10 w-10 grid place-items-center rounded-full hover:bg-slate-100"><Trash2 className="h-4 w-4" /></button>
          </div>
        );
      })}
      <button data-testid="schedule-add-btn" onClick={() => { setEdit(null); setOpen(true); }} className="pl-btn-ghost w-full"><Plus className="h-4 w-4" />Add care task</button>
      <ScheduleDialog open={open} onOpenChange={setOpen} petId={petId} schedule={edit} onSaved={reload} />
    </div>
  );
}

export default function PetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pet, setPet] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [household, setHousehold] = useState(null);
  const [remOpen, setRemOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const [p, s, r, h] = await Promise.all([api.get(`/pets/${id}`), api.get(`/schedules?pet_id=${id}`), api.get(`/reminders?pet_id=${id}`), api.get("/household")]);
      setPet(p.data); setSchedules(s.data); setReminders(r.data); setHousehold(h.data);
    } catch (err) { toast.error(errMsg(err)); nav("/app/pets"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!pet) return <div className="pl-card h-60 animate-pulse" />;

  const removePet = async () => {
    if (!window.confirm(`Delete ${pet.name} and all their schedules, reminders and records?`)) return;
    await api.delete(`/pets/${id}`);
    nav("/app/pets");
  };

  return (
    <div data-testid="pet-detail-page">
      <div className="flex items-center gap-4 mb-5">
        <PetAvatar pet={pet} size={96} ring />
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-4xl font-extrabold truncate">{pet.name}</h1>
          <p className="text-slate-500 capitalize">{[pet.species, pet.breed, pet.weight].filter(Boolean).join(" · ")}</p>
        </div>
        <Link to={`/app/pets/${id}/edit`} className="h-11 w-11 grid place-items-center rounded-full bg-white border" data-testid="pet-edit-btn"><Pencil className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Link to={`/app/emergency?pet=${id}`} className="pl-btn-ghost !bg-[#FDF2F0] !border-[#F6D5CC] text-[#C2410C]" data-testid="pet-emergency-btn"><Siren className="h-4 w-4" />Emergency card</Link>
        <Link to={`/app/guide?pet=${id}`} className="pl-btn-ghost" data-testid="pet-guide-btn"><FileText className="h-4 w-4" />Sitter guide</Link>
      </div>
      <Tabs defaultValue="care">
        <TabsList className="w-full grid grid-cols-5 h-12 rounded-2xl bg-[#EFECE6]">
          <TabsTrigger value="care" data-testid="pet-tab-care" className="rounded-xl">Care</TabsTrigger>
          <TabsTrigger value="faves" data-testid="pet-tab-faves" className="rounded-xl">Faves</TabsTrigger>
          <TabsTrigger value="due" data-testid="pet-tab-due" className="rounded-xl">Due</TabsTrigger>
          <TabsTrigger value="records" data-testid="pet-tab-records" className="rounded-xl">Records</TabsTrigger>
          <TabsTrigger value="profile" data-testid="pet-tab-profile" className="rounded-xl">Profile</TabsTrigger>
        </TabsList>
        <TabsContent value="care" className="mt-4"><ScheduleList petId={id} schedules={schedules} reload={load} /></TabsContent>
        <TabsContent value="due" className="mt-4 space-y-3">
          {reminders.map((r) => <ReminderRow key={r.id} r={r} onToggle={async (x) => { await toggleReminder(x); load(); }} onDelete={async (x) => { await api.delete(`/reminders/${x.id}`); load(); }} />)}
          {reminders.length === 0 && <p className="text-sm text-slate-500">No vaccines or preventives tracked yet. Upload a vet record or add one.</p>}
          <button className="pl-btn-ghost w-full" data-testid="pet-add-reminder-btn" onClick={() => setRemOpen(true)}><Plus className="h-4 w-4" />Add reminder</button>
          <ReminderDialog open={remOpen} onOpenChange={setRemOpen} petId={id} onSaved={load} />
        </TabsContent>
        <TabsContent value="faves" className="mt-4"><PetItems petId={id} /></TabsContent>
        <TabsContent value="records" className="mt-4"><VetRecords petId={id} onChange={load} /></TabsContent>
        <TabsContent value="profile" className="mt-4">
          {household && <GuidePet pet={{ ...pet, schedules, favorites: [] }} household={household} />}
          <button onClick={removePet} className="text-sm text-[#C2410C] font-semibold" data-testid="pet-delete-btn">Delete {pet.name}</button>
        </TabsContent>
      </Tabs>
      <p className="text-[11px] text-slate-400 mt-8">{VET_LINE}</p>
    </div>
  );
}
